import React from 'react';
import PropTypes from 'prop-types';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Icon from '../Icon';
import styled from 'styled-components';
import uuid from '@afosto/utils/uuid';
import * as defaultRenderers from '../FiltersField/renderers';

const FiltersButton = styled(IconButton)`
  && {
    margin-right: ${props => props.theme.spacing()}px;
  }
`;

const NewFilterButton = styled(IconButton)`
  && {
    background-color: #999;
    margin-left: ${props => props.theme.spacing()}px;

    .MuiSvgIcon-root {
      color: #fff;
    }
  }
`;

const DataGridFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const DataGridFilters = ({ filterRenderers }) => {
  const { setShowFilterSelection, filters: filtersModule = {} } = useDataSourceContext();
  const {
    activeFilters,
    filters,
    createFilterSelection,
    loadFilterSelection,
    hasAvailableFilters,
    removeActiveFilter,
    getActiveFilter,
    updateActiveFilter,
  } = filtersModule;
  const renderers = { ...defaultRenderers.default, ...filterRenderers };

  /**
   * Open the filter selection drawer and show the create new filter selection.
   */
  const handleNewFilterSelection = () => {
    createFilterSelection();
    setShowFilterSelection(true);
  };

  /**
   * Open the filter selection drawer and show the selected filter selection.
   * @param {Object} filterSelection
   */
  const handleClickFilterSelection = filterSelection => () => {
    loadFilterSelection(filterSelection.key);
    setShowFilterSelection(true);
  };

  /**
   * Open the filter selection drawer.
   */
  const handleOpenFilterSelection = () => {
    setShowFilterSelection(true);
  };

  /**
   * Remove the filter option from the active filter or remove the filter.
   * @param filterKey
   * @param option
   */
  const handleRemoveActiveFilterOption = (filterKey, option) => {
    const activeFilter = getActiveFilter(filterKey);

    if (activeFilter?.value?.length <= 1 || ['missing, between'].includes(activeFilter?.operator)) {
      removeActiveFilter(filterKey);
    } else {
      const newValue = (activeFilter?.value ?? []).filter(value => value !== option);
      updateActiveFilter({ ...activeFilter, value: newValue });
    }
  };

  /**
   * Return the filter selection elements.
   * @returns {array}
   */
  const getFilterSelections = () =>
    activeFilters.reduce((acc, filterSelection) => {
      const filter = filters.find(({ key: filterKey }) => filterKey === filterSelection.key);

      if (filter) {
        const { name, type } = filter;
        const { operator, value } = filterSelection;
        const FilterRenderer = renderers[type] ?? renderers.default;

        if (['in', 'not_in'].includes(operator)) {
          const filterValues = (value ?? []).reduce(
            (filterValueAcc, filterValue) => [
              ...filterValueAcc,
              <Grid item key={`${filterSelection.key}_${uuid()}`}>
                <Chip
                  label={
                    <FilterRenderer
                      name={name}
                      operator={operator === 'in' ? 'is' : 'not_is'}
                      value={[filterValue]}
                      renderSettings={{ shorthandOperator: true }}
                    />
                  }
                  onClick={handleClickFilterSelection(filterSelection)}
                  onDelete={() => handleRemoveActiveFilterOption(filterSelection.key, filterValue)}
                />
              </Grid>,
            ],
            [],
          );

          return [...acc, ...filterValues];
        }

        return [
          ...acc,
          <Grid item key={filterSelection.key}>
            <Chip
              label={
                <FilterRenderer
                  name={name}
                  operator={operator}
                  value={value}
                  renderSettings={{ shorthandOperator: true }}
                />
              }
              onClick={handleClickFilterSelection(filterSelection)}
              onDelete={() => handleRemoveActiveFilterOption(filterSelection.key, value)}
            />
          </Grid>,
        ];
      }

      return [...acc];
    }, []);

  return (
    <DataGridFiltersContainer>
      <FiltersButton size="small" onClick={handleOpenFilterSelection}>
        <Icon icon={['fal', 'filter']} size="small" />
      </FiltersButton>
      <Grid container spacing={1} style={{ width: 'auto' }}>
        {getFilterSelections()}
      </Grid>
      {hasAvailableFilters() && (
        <NewFilterButton size="small" onClick={handleNewFilterSelection}>
          <Icon icon={['fal', 'plus']} size="small" />
        </NewFilterButton>
      )}
    </DataGridFiltersContainer>
  );
};

DataGridFilters.propTypes = {
  filterRenderers: PropTypes.object,
};

DataGridFilters.defaultProps = {
  filterRenderers: {},
};

export default DataGridFilters;
